import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { Input, Space, Button, Tooltip } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from 'react-highlight-words';
import { IAppUserWithStats } from "../../interfaces";
import AppUsersService from "../../services/http/services/AppUsersService";
import { EmitError } from "../../utils/EmitError";
import { formatCpf, formatPhone, onlyNumbers } from "../../utils/Masks";
import { convertDataBrParaUS, convertMoedaBRDoJSNovo, formatDateStringToDateAndTime, poeValorComPontosEmMil } from "../../utils/Conversores";
import useAuth from "../../hooks/useAuth";
import { useGetColumnSearchPropsData } from "../../hooks/FilterTable/date";
import { useGetColumnSearchProps } from "../../hooks/FilterTable/stringClearFiltros";
import useModules from '../../hooks/useModules';
import { getSortOrder } from "../../hooks/SortOrder";
import { ThemeContext } from "../../contexts/ThemeContext";
import { useTableParams } from "../../hooks/useTableParams";
import TableActionButton from "../../components/TableActionButton";
import { IActiveFilterItem } from "../../components/ActiveFiltersBar";

export interface ITableData extends IAppUserWithStats {
    key: number | undefined;
    createdAt: string;
    last_access?: string;
    stats: {
        taxes: number;
        invoices: number;
        tickets: number;
    }
}

export type TypeSortAppUser = 'name' | 'createdAt' | 'last_access' | 'total_tickets' | 'total_invoices' | 'total_taxes';

export interface IAppUserFilters {
    name: string | null;
    cpf: string | null;
    phone: string | null;
    createdAt: string | null;
}

export default function useAppUsers() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const navigate = useNavigate();
    const { activeCity } = useAuth();
    const { hasTaxesModule } = useModules();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [tableData, setTableData] = useState<Array<ITableData>>([]);
    const [totalAppUsers, setTotalAppUsers] = useState<number>(0);

    const {
        tableParams,
        handleTableChange,
        handleCustomChange,
        handlePaginationChange,
        handleFilterClear,
        handleClearAllFilters,
        lastSorterRef,
    } = useTableParams<IAppUserFilters, TypeSortAppUser>({
        storageKey: '@NotaMunicipio:appUserFilters',
        defaultFilters: {
            name: null,
            cpf: null,
            phone: null,
            createdAt: null,
        },
        defaultSort: {
            field: 'createdAt',
            order: 'desc',
        },
    });

    const [searchName, setSearchName] = useState(tableParams.filters.name || '');
    const [searchCpf, setSearchCpf] = useState(tableParams.filters.cpf || '');

    const refFuncaoLimparFiltrosString = useRef<any>(null);
    const nameInputRef = useRef<any>(null);

    useEffect(() => {
        setSearchName(tableParams.filters.name || '');
    }, [tableParams.filters.name]);

    useEffect(() => {
        setSearchCpf(tableParams.filters.cpf || '');
    }, [tableParams.filters.cpf]);

    const activeFilterItems: Array<IActiveFilterItem | null> = [
        tableParams.filters.name
            ? { key: 'name', label: 'Nome', value: tableParams.filters.name, onClear: () => handleFilterClear('name') }
            : null,
        tableParams.filters.cpf
            ? { key: 'cpf', label: 'CPF', value: formatCpf(tableParams.filters.cpf), onClear: () => handleFilterClear('cpf') }
            : null,
        tableParams.filters.phone
            ? { key: 'phone', label: 'Telefone', value: formatPhone(tableParams.filters.phone), onClear: () => handleFilterClear('phone') }
            : null,
        tableParams.filters.createdAt
            ? { key: 'createdAt', label: 'Data', value: tableParams.filters.createdAt, onClear: () => handleFilterClear('createdAt') }
            : null,
    ];

    const columns = [
        {
            title: 'Nome / CPF',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            showSorterTooltip: false,
            sortOrder: getSortOrder('name', tableParams.sorter),
            filterDropdown: ({ confirm, clearFilters }: any) => (
                <div style={{ padding: 12, width: 260, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 4 }}>
                            Nome
                        </div>
                        <Input
                            ref={nameInputRef}
                            placeholder="Buscar por nome..."
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            onPressEnter={() => {
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: { name: searchName || null, cpf: searchCpf || null },
                                });
                            }}
                            allowClear
                        />
                    </div>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 4 }}>
                            CPF
                        </div>
                        <Input
                            placeholder="000.000.000-00"
                            value={searchCpf}
                            onChange={(e) => setSearchCpf(formatCpf(e.target.value))}
                            onPressEnter={() => {
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: { name: searchName || null, cpf: searchCpf || null },
                                });
                            }}
                            allowClear
                        />
                    </div>
                    <Space style={{ justifyContent: 'space-between', marginTop: 4 }}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            size="small"
                            onClick={() => {
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: { name: searchName || null, cpf: searchCpf || null },
                                });
                            }}
                            style={{ minWidth: 100 }}
                        >
                            Pesquisar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                setSearchName('');
                                setSearchCpf('');
                                if (clearFilters) clearFilters();
                                confirm();
                                handleCustomChange({
                                    newPage: 1,
                                    newFilters: { name: null, cpf: null },
                                });
                            }}
                            style={{ minWidth: 80 }}
                        >
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: () => (
                <SearchOutlined style={{ color: (tableParams.filters.name || tableParams.filters.cpf) ? '#1677ff' : undefined }} />
            ),
            onFilterDropdownOpenChange: (visible: boolean) => {
                if (visible) {
                    setTimeout(() => nameInputRef.current?.focus(), 100);
                }
            },
            render: (_: any, record: ITableData) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontWeight: 600, color: isDark ? '#f0f0f0' : '#1e293b', fontSize: 13, lineHeight: '1.3' }}>
                        {tableParams.filters.name ? (
                            <Highlighter
                                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                                searchWords={[tableParams.filters.name]}
                                autoEscape
                                textToHighlight={record.name || '--'}
                            />
                        ) : (
                            record.name || '--'
                        )}
                    </span>
                    <span style={{ color: isDark ? '#8c8c8c' : '#64748b', fontSize: 12, fontWeight: 500, letterSpacing: '0.3px', whiteSpace: 'nowrap', lineHeight: '1.2' }}>
                        {tableParams.filters.cpf ? (
                            <Highlighter
                                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                                searchWords={[tableParams.filters.cpf]}
                                autoEscape
                                textToHighlight={record.cpf ? formatCpf(record.cpf) : '--'}
                            />
                        ) : (
                            record.cpf ? formatCpf(record.cpf) : '--'
                        )}
                    </span>
                </div>
            ),
            width: '24%'
        },
        {
            title: 'Telefone',
            dataIndex: 'phone',
            key: 'phone',
            ...useGetColumnSearchProps({ dataIndex: 'phone', ref: refFuncaoLimparFiltrosString, formatter: formatPhone, initialValue: tableParams.filters.phone }),
            render: (phone: string) => (
                <span style={{ color: isDark ? '#a6a6a6' : '#64748b', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {tableParams.filters.phone ? (
                        <Highlighter
                            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                            searchWords={[tableParams.filters.phone]}
                            autoEscape
                            textToHighlight={phone ? formatPhone(phone) : '--'}
                        />
                    ) : (
                        phone ? formatPhone(phone) : '--'
                    )}
                </span>
            ),
            width: '12%'
        },
        {
            title: 'Data de Cadastro',
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: true,
            sortOrder: getSortOrder('createdAt', tableParams.sorter),
            ...useGetColumnSearchPropsData({ dataIndex: 'createdAt', ref: refFuncaoLimparFiltrosString, onFilterClear: () => handleFilterClear('createdAt') }),
            render: (criado: any) => (
                <span style={{ color: isDark ? '#8c8c8c' : '#64748b', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {criado ? formatDateStringToDateAndTime(criado, true) : '--'}
                </span>
            ),
            showSorterTooltip: false,
            width: '13%'
        },
        {
            title: 'Último Acesso',
            dataIndex: 'last_access',
            key: 'last_access',
            sorter: true,
            sortOrder: getSortOrder('last_access', tableParams.sorter),
            showSorterTooltip: false,
            render: (lastAccess: any) => (
                <span style={{ color: isDark ? '#8c8c8c' : '#64748b', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {lastAccess ? formatDateStringToDateAndTime(lastAccess, true) : '--'}
                </span>
            ),
            width: '13%'
        },
        {
            title: 'Bilhetes',
            dataIndex: 'total_tickets',
            key: 'total_tickets',
            sorter: true,
            sortOrder: getSortOrder('total_tickets', tableParams.sorter),
            showSorterTooltip: false,
            align: 'center' as const,
            render: (value?: number) => (
                <Tooltip color="#1677ff" title="Total de bilhetes gerados">
                    <span style={{ fontWeight: 600, color: '#1677ff', fontSize: 14, whiteSpace: 'nowrap' }}>
                        {poeValorComPontosEmMil(value) ?? '0'}
                    </span>
                </Tooltip>
            ),
            width: '7%'
        },
        {
            title: 'Notas',
            dataIndex: 'total_invoices',
            key: 'total_invoices',
            sorter: true,
            sortOrder: getSortOrder('total_invoices', tableParams.sorter),
            showSorterTooltip: false,
            align: 'right' as const,
            render: (value?: number) => (
                <Tooltip color="#389e0d" title="Total das notas válidas importadas">
                    <span style={{ fontWeight: 600, color: isDark ? '#52c41a' : '#237804', fontSize: 14, whiteSpace: 'nowrap' }}>
                        R$ {convertMoedaBRDoJSNovo(value) ?? '0'}
                    </span>
                </Tooltip>
            ),
            width: '12%'
        },
        {
            title: 'Impostos',
            hidden: !hasTaxesModule,
            dataIndex: 'total_taxes',
            key: 'total_taxes',
            sorter: true,
            showSorterTooltip: false,
            align: 'right' as const,
            render: (value?: number) => (
                <Tooltip color="#d46b08" title="Total dos impostos válidos">
                    <span style={{ fontWeight: 600, color: isDark ? '#fa8c16' : '#d46b08', fontSize: 14, whiteSpace: 'nowrap' }}>
                        R$ {convertMoedaBRDoJSNovo(value) ?? '0'}
                    </span>
                </Tooltip>
            ),
            width: '12%'
        },
        {
            title: 'Ações',
            key: 'actions',
            width: '5%',
            align: 'center' as const,
            render: (_: any, record: any) => (
                <TableActionButton
                    action="view"
                    tooltip="Visualizar detalhes"
                    onClick={() => navigate(`/usuarios/aplicativo/visualizar/${record.id}`)}
                />
            )
        },
    ].filter((column) => !column?.hidden);

    const setTableDataFromRequest = useCallback((data: Array<IAppUserWithStats>) => {
        const newData = data.map((user) => ({
            ...user,
            key: user.id,
            stats: {
                tickets: user.total_tickets,
                invoices: user.total_invoices,
                taxes: user.total_taxes
            },
            createdAt: user.created_at,
            last_access: user.last_access,
        }));
        setTableData(newData);
    }, []);

    const handleExport = async (type: 'excel' | 'pdf') => {
        if (!activeCity?.id) return;

        try {
            const { blob, filename } = await AppUsersService.exportAppUsers({
                cityId: activeCity?.id,
                type,
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Erro ao exportar usuários:', error);
        }
    };

    useEffect(() => {
        const controller = new AbortController();
        async function fetchAppUsers() {
            try {
                setIsLoading(true);
                const res = await AppUsersService.listAppUsersWithPaginate({
                    cityId: activeCity?.id!,
                    page: tableParams.pagination.current,
                    size: tableParams.pagination.pageSize,
                    sort: tableParams.sorter.field ? tableParams.sorter.field as TypeSortAppUser : 'createdAt',
                    order: tableParams.sorter.order === 'asc' ? 'asc' : 'desc',
                    name: tableParams.filters?.name || undefined,
                    cpf: onlyNumbers(tableParams.filters?.cpf) || undefined,
                    phone: onlyNumbers(tableParams.filters?.phone) || undefined,
                    createdAt: (tableParams.filters?.createdAt && convertDataBrParaUS(tableParams.filters.createdAt)) || undefined,
                    signal: controller.signal,
                });

                setTableDataFromRequest(res.appUsers);
                setTotalAppUsers(res.total);
                setIsLoading(false);
            } catch (err) {
                if (!(err instanceof AxiosError && err.name === 'CanceledError')) {
                    EmitError(err, true, 'appUsers_loadAppUsers');
                }
            }
        }

        fetchAppUsers();

        return () => controller.abort();
    }, [activeCity, tableParams, setTableDataFromRequest]);

    return {
        isLoading,
        columns,
        tableData,
        navigate,
        totalAppUsers,
        tableParams,
        activeFilterItems,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        handleExport,
        lastSorterRef,
    };
}
